// src/modules/operaciones/inventarios/hooks/usePreciosAlertas.js
//
// Carga de alertas de precios (/precios/alertas) con polling mientras el backend calcula,
// y manejo del detalle por bodega de un artículo (abrirDetalle / cerrarDetalle).
// Parámetros: estado = filtro de estado enviado al backend, recarga = contador que fuerza una nueva consulta.
import { useState, useEffect, useRef, useCallback } from 'react'; // Hooks usados
import { API_URL, AVISO_LENTO_MS, pedirJson } from '../utils/preciosComun'; // Constantes y GET JSON

export default function usePreciosAlertas({ estado, recarga }) {
  const [articulos, setArticulos] = useState([]); // Alertas devueltas por el backend
  const [meta, setMeta] = useState(null); // Metadatos de la respuesta (calculando, truncado, totales, límite)
  const [loading, setLoading] = useState(true); // true mientras se consulta o el backend sigue calculando
  const [lento, setLento] = useState(false); // true si la carga supera AVISO_LENTO_MS
  const [error, setError] = useState(null); // Mensaje de error de la carga principal
  const [articuloDetalle, setArticuloDetalle] = useState(null); // Artículo abierto en el modal de detalle; null = cerrado

  const detalleCtrlRef = useRef(null); // AbortController de la petición de detalle en curso

  /* === Detalle de artículo === */
  // Cierra el modal y cancela la petición de detalle en curso
  const cerrarDetalle = useCallback(() => {
    detalleCtrlRef.current?.abort();
    detalleCtrlRef.current = null;
    setArticuloDetalle(null);
  }, []);

  // Abre el modal y consulta las existencias por bodega del artículo. Cancela cualquier detalle anterior.
  const abrirDetalle = useCallback(async (articulo) => {
    detalleCtrlRef.current?.abort();
    const controller = new AbortController();
    detalleCtrlRef.current = controller;
    const sigueAbierto = (prev) => prev && prev.CODIGO === articulo.CODIGO; // Evita pisar el estado si el usuario ya abrió otro artículo
    setArticuloDetalle({ ...articulo, bodegas: null, cargandoDetalle: true, errorDetalle: null }); // Abre el modal en estado "cargando"
    try {
      const data = await pedirJson(`${API_URL}/precios/detalle/${encodeURIComponent(articulo.CODIGO)}`, controller.signal);
      if (controller.signal.aborted) return;
      setArticuloDetalle((prev) => (sigueAbierto(prev) ? {
        ...articulo,
        EXISTENCIA_TOTAL: data.articulo?.EXISTENCIA_TOTAL ?? articulo.EXISTENCIA_TOTAL, // Prefiere el total fresco del detalle
        bodegas: Array.isArray(data.bodegas) ? data.bodegas : [],
        cargandoDetalle: false,
        errorDetalle: null,
      } : prev));
    } catch (err) {
      if (controller.signal.aborted) return;
      setArticuloDetalle((prev) => (sigueAbierto(prev) ? { ...articulo, bodegas: [], cargandoDetalle: false, errorDetalle: err.message } : prev));
    }
  }, []);

  // Al desmontar el componente, cancela cualquier petición de detalle pendiente
  useEffect(() => () => detalleCtrlRef.current?.abort(), []);

  /* --- Carga principal con Polling --- */
  // Depende de `estado` (filtro del backend) y `recarga` (botón Recargar). Consulta /precios/alertas y,
  // si el backend responde `meta.calculando`, repite la consulta cada 5 s hasta que los datos estén listos.
  useEffect(() => {
    const controller = new AbortController(); // Permite cancelar el fetch al cambiar de filtro o desmontar
    let cancelado = false; // Bandera para ignorar respuestas tardías
    let pollTimeout; // Temporizador del siguiente sondeo

    const fetchData = async () => {
      if (cancelado) return;
      setLoading(true);
      setLento(false);
      
      const timerLento = setTimeout(() => { if (!cancelado) setLento(true); }, AVISO_LENTO_MS); // Activa el aviso de carga lenta

      try {
        const res = await fetch(`${API_URL}/precios/alertas?estado=${encodeURIComponent(estado)}`, { signal: controller.signal });
        const data = await res.json().catch(() => ({}));
        
        if (!res.ok || !data.success) throw new Error(data.error || `HTTP ${res.status}`);
        if (cancelado) return;

        clearTimeout(timerLento);
        setLento(false);
        setArticulos(Array.isArray(data.data) ? data.data : []);
        setMeta(data.meta ?? null);
        setError(null);

        // Si el backend está calculando en segundo plano, consultamos de nuevo en 5 segundos
        if (data.meta && data.meta.calculando) {
          pollTimeout = setTimeout(fetchData, 5000);
        } else {
          setLoading(false);
        }
      } catch (err) {
        if (cancelado || err.name === 'AbortError') return; // Cancelación voluntaria: no es un error
        clearTimeout(timerLento);
        setArticulos([]);
        setMeta(null);
        setError(err.message);
        setLoading(false);
      }
    };

    fetchData();

    // Limpieza: cancela la petición y el sondeo pendiente
    return () => { 
      cancelado = true; 
      controller.abort(); 
      clearTimeout(pollTimeout);
    };
  }, [estado, recarga]);

  // Valores y funciones que expone el hook
  return { articulos, meta, loading, lento, error, articuloDetalle, abrirDetalle, cerrarDetalle };
}